import React, { useEffect, useState, RefObject, useRef } from "react";
import { Box, Portal } from "@chakra-ui/react";
import { TourStep } from "@hooks/useTourGuide";
import { TourGuideTooltip } from "./TourGuideTooltip";

export interface TourGuideProps {
  isActive: boolean;
  currentStep: TourStep;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  targetRef?: RefObject<HTMLElement>;
  targetSelector?: string;
  borderRadius?: string;
}

interface HighlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
}

export const TourGuide: React.FC<TourGuideProps> = ({
  isActive,
  currentStep,
  currentStepIndex,
  totalSteps,
  onNext,
  onBack,
  onClose,
  targetRef,
  targetSelector,
  borderRadius = "8px",
}) => {
  const [highlightBox, setHighlightBox] = useState<HighlightBox>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
  });
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    let element: HTMLElement | null = null;

    if (targetRef && targetRef.current) {
      element = targetRef.current;
    } else if (targetSelector) {
      element = document.querySelector(targetSelector);
    }

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    // Add padding around the element for better highlight fit
    // Use custom padding from step if provided, otherwise default to 8
    const highlightPadding = currentStep.highlightPadding ?? 8;

    // Update highlight box with padding
    setHighlightBox({
      top: rect.top + scrollY - highlightPadding,
      left: rect.left + scrollX - highlightPadding,
      width: rect.width + (highlightPadding * 2),
      height: rect.height + (highlightPadding * 2),
    });

    // Calculate tooltip position
    const tooltipWidth = tooltipRef.current?.offsetWidth || 400;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
    const offset = currentStep.offset || { x: 20, y: 0 };
    const position = currentStep.position || "right";

    let tooltipTop = 0;
    let tooltipLeft = 0;

    switch (position) {
      case "right":
        tooltipLeft = rect.right + scrollX + offset.x;
        tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2) + offset.y;
        break;
      case "left":
        tooltipLeft = rect.left + scrollX - tooltipWidth + offset.x;
        tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2) + offset.y;
        break;
      case "top":
        tooltipLeft = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2) + offset.x;
        tooltipTop = rect.top + scrollY - tooltipHeight + offset.y;
        break;
      case "bottom":
        tooltipLeft = rect.left + scrollX + (rect.width / 2) - (tooltipWidth / 2) + offset.x;
        tooltipTop = rect.bottom + scrollY + offset.y;
        break;
      case "center":
        tooltipLeft = window.innerWidth / 2 - tooltipWidth / 2 + scrollX;
        tooltipTop = window.innerHeight / 2 - tooltipHeight / 2 + scrollY;
        break;
      default:
        tooltipLeft = rect.right + scrollX + offset.x;
        tooltipTop = rect.top + scrollY + (rect.height / 2) - (tooltipHeight / 2) + offset.y;
    }

    // Keep tooltip in viewport
    if (tooltipLeft + tooltipWidth > window.innerWidth) {
      tooltipLeft = window.innerWidth - tooltipWidth - 20;
    }
    if (tooltipLeft < 0) {
      tooltipLeft = 20;
    }
    if (tooltipTop + tooltipHeight > window.innerHeight + scrollY) {
      tooltipTop = window.innerHeight + scrollY - tooltipHeight - 20;
    }
    if (tooltipTop < scrollY) {
      tooltipTop = scrollY + 20;
    }

    setTooltipPosition({
      top: tooltipTop,
      left: tooltipLeft,
    });
  };

  useEffect(() => {
    if (!isActive) return;

    updatePosition();

    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isActive, currentStep, targetRef, targetSelector, currentStepIndex]);

  if (!isActive) {
    return null;
  }

  return (
    <Portal>
      {/* Full Screen Overlay with SVG Mask for Spotlight */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex={9999}
        pointerEvents="none"
      >
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <defs>
            <mask id="tour-spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={highlightBox.left}
                y={highlightBox.top}
                width={highlightBox.width}
                height={highlightBox.height}
                rx={borderRadius}
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.5)"
            mask="url(#tour-spotlight-mask)"
          />
        </svg>
      </Box>

      {/* Highlight Border */}
      <Box
        position="fixed"
        top={`${highlightBox.top}px`}
        left={`${highlightBox.left}px`}
        width={`${highlightBox.width}px`}
        height={`${highlightBox.height}px`}
        border="3px solid white"
        borderRadius={borderRadius}
        boxShadow="0 0 0 2px rgba(255, 255, 255, 0.3)"
        zIndex={10000}
        pointerEvents="none"
      />

      {/* Tooltip */}
      <Box
        ref={tooltipRef}
        position="absolute"
        top={`${tooltipPosition.top}px`}
        left={`${tooltipPosition.left}px`}
        zIndex={10000}
      >
        <TourGuideTooltip
          title={currentStep.title}
          description={currentStep.description}
          currentStepIndex={currentStepIndex}
          totalSteps={totalSteps}
          onNext={onNext}
          onBack={onBack}
          onClose={onClose}
        />
      </Box>
    </Portal>
  );
};
